import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Calendar,
  FileText,
  Users,
  BarChart3,
  Shield,
  ArrowRight,
  CheckCircle,
  Building2,
  Smartphone,
  Monitor,
  Loader2
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { usePricing, formatPrice } from '../hooks/useHomepageData';

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { pricing, isLoading: pricingLoading, currency } = usePricing();
  const lang = i18n.language?.substring(0, 2) || 'id';

  const features = [
    {
      icon: Clock,
      titleKey: 'features.attendance.title',
      descriptionKey: 'features.attendance.description',
    },
    {
      icon: Calendar,
      titleKey: 'features.leave.title',
      descriptionKey: 'features.leave.description',
    },
    {
      icon: FileText,
      titleKey: 'features.approval.title',
      descriptionKey: 'features.approval.description',
    },
    {
      icon: Users,
      titleKey: 'features.organization.title',
      descriptionKey: 'features.organization.description',
    },
    {
      icon: BarChart3,
      titleKey: 'features.dashboard.title',
      descriptionKey: 'features.dashboard.description',
    },
    {
      icon: Shield,
      titleKey: 'features.security.title',
      descriptionKey: 'features.security.description',
    },
  ];

  const platforms = [
    {
      icon: Monitor,
      titleKey: 'platforms.web.title',
      descriptionKey: 'platforms.web.description',
    },
    {
      icon: Smartphone,
      titleKey: 'platforms.mobile.title',
      descriptionKey: 'platforms.mobile.description',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Building2 className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">Reko HR</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-primary-600">{t('nav.features')}</a>
              <a href="#platforms" className="text-gray-700 hover:text-primary-600">{t('nav.platforms')}</a>
              <a href="#pricing" className="text-gray-700 hover:text-primary-600">{t('nav.pricing')}</a>
              <a href="#contact" className="text-gray-700 hover:text-primary-600">{t('nav.contact')}</a>
              <Link to="/about" className="text-gray-700 hover:text-primary-600">{t('nav.about')}</Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex items-center space-x-4">
              <div className="md:hidden">
                <LanguageSwitcher />
              </div>
              <Link
                to="/register"
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
              >
                {t('nav.individualSignUp')}
              </Link>
              <Link
                to="/company-request"
                className="hidden sm:block border border-primary-600 text-primary-600 px-4 py-2 rounded-md hover:bg-primary-50 transition-colors"
              >
                {t('nav.enterpriseInquiry')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t('hero.title')}
            <br />
            <span className="text-primary-600">{t('hero.brand')}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('hero.description')}
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/register"
              className="bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              {t('hero.getStarted')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="#demo"
              className="border-2 border-primary-600 text-primary-600 px-8 py-4 rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('hero.viewDemo')}
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('features.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <feature.icon className="h-12 w-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-600">
                  {t(feature.descriptionKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section id="platforms" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('platforms.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('platforms.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <platform.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t(platform.titleKey)}
                </h3>
                <p className="text-gray-600">
                  {t(platform.descriptionKey)}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pricing.subtitle')}
            </p>
          </div>

          {pricingLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pricing.map((plan) => (
                <div
                  key={plan.plan_code}
                  className={`bg-white p-8 rounded-xl shadow-md ${
                    plan.is_popular ? 'border-2 border-primary-600 relative' : ''
                  }`}
                >
                  {plan.is_popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm">
                        {plan.badge_text || t('pricing.popular')}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="text-4xl font-bold text-primary-600 mb-4">
                    {formatPrice(plan.price, currency, lang)}
                    {!plan.is_contact_only && (
                      <span className="text-lg font-normal text-gray-600">{t('pricing.perUser')}</span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.is_contact_only ? (
                    <a
                      href="#contact"
                      className="w-full border border-primary-600 text-primary-600 py-3 px-4 rounded-lg text-center block hover:bg-primary-50 transition-colors"
                    >
                      {t('pricing.contactUs')}
                    </a>
                  ) : (
                    <Link
                      to={`/company-request?plan=${plan.plan_code.toLowerCase()}`}
                      className={`w-full py-3 px-4 rounded-lg text-center block transition-colors ${
                        plan.is_popular
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      {t('pricing.getStarted')}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('contact.description')}
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/company-request"
              className="bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              {t('contact.enterpriseInquiry')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="mailto:contact@reko-hr.com"
              className="border-2 border-primary-600 text-primary-600 px-8 py-4 rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('contact.emailInquiry')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Building2 className="h-8 w-8 text-primary-400" />
                <span className="ml-2 text-2xl font-bold">Reko HR</span>
              </div>
              <p className="text-gray-400">
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">{t('nav.features')}</a></li>
                <li><a href="#platforms" className="hover:text-white">{t('nav.platforms')}</a></li>
                <li><a href="#pricing" className="hover:text-white">{t('nav.pricing')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.support')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">{t('footer.helpCenter')}</a></li>
                <li><a href="#" className="hover:text-white">{t('footer.apiDocs')}</a></li>
                <li><a href="#contact" className="hover:text-white">{t('footer.contactUs')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.company')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white">{t('footer.aboutUs')}</Link></li>
                <li><a href="#" className="hover:text-white">{t('footer.privacyPolicy')}</a></li>
                <li><a href="#" className="hover:text-white">{t('footer.termsOfService')}</a></li>
              </ul>
              <div className="mt-4">
                <LanguageSwitcher variant="footer" />
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
